<script setup lang="ts">
import ElTag, { ElTagColor } from './ElTag.vue';
import ElIcon, { ElIconProps } from './ElIcon.vue';
import ElInputCheckbox from './forms/ElInputCheckbox.vue';
import ElTextCell, { ElTextCellColor } from '@/ElTextCell.vue';
import ElAvatar from './ElAvatar.vue';
import { ElTextCellStyle } from './ElTextCell.vue';

export interface ElListItemProps {
  avatar?: { picture: string; label?: string } | { picture?: string; label: string };
  time?: string;
  mainText: {
    label: string;
    style?: ElTextCellStyle;
    color?: ElTextCellColor;
  };
  secondaryRow?: string;
  tertiaryRow?: string;
  trailingIcon?: ElIconProps;
  amount?: string;
  tags?: {
    key?: string;
    text: string;
    color?: ElTagColor;
  }[];
  modelValue?: boolean;
  color: 'primary' | 'secondary';
}

const props = defineProps<ElListItemProps>();

const $emits = defineEmits<{
  (e: 'update:modelValue', checked: boolean | undefined): void;
  (e: 'click', payload: MouseEvent): void;
}>();
</script>
<template>
  <li
    class="px-4 pt-4 flex flex-row w-full active:bg-neutral-pressed transition-colors min-w-80"
    :class="{
      'bg-primary-light-active': props.color === 'primary' && props.modelValue,
      'bg-secondary-light-active': props.color === 'secondary' && props.modelValue,
      'bg-neutral-surface': !props.modelValue,
    }"
    @click="
      e => {
        $emit('click', e);
        props.modelValue !== undefined && $emit('update:modelValue', !props.modelValue);
      }
    "
  >
    <div v-if="props.modelValue !== undefined" class="mr-5 pb-4 flex flex-col items-center justify-center">
      <ElInputCheckbox :color="props.color" :model-value="props.modelValue" />
    </div>
    <div class="flex flex-col flex-grow overflow-x-hidden">
      <div class="flex flex-row overflow-x-hidden">
        <div v-if="props.avatar !== undefined" class="mr-3 flex flex-col justify-center">
          <ElAvatar :picture="props.avatar.picture" :label="props.avatar.label" size="sm" />
        </div>
        <div class="flex flex-row justify-between flex-grow overflow-x-hidden gap-4">
          <div class="flex flex-col gap-1 text-sm flex-grow overflow-x-hidden">
            <span v-if="props.mainText" class="whitespace-nowrap text-ellipsis overflow-x-hidden w-full">
              <ElTextCell v-bind="props.mainText" />
            </span>
            <span v-if="props.secondaryRow" class="text-neutral-darker whitespace-nowrap text-ellipsis overflow-hidden w-full">{{
              props.secondaryRow
            }}</span>
            <span v-if="props.tertiaryRow" class="text-neutral-lighter whitespace-nowrap text-ellipsis overflow-hidden w-full">{{
              props.tertiaryRow
            }}</span>
          </div>
          <div class="text-right gap-1.5 flex flex-col flex-shrink-0">
            <span v-if="props.time" class="text-neutral-darker text-xs">{{ props.time }}</span>
            <div class="flex flex-row gap-2 justify-end">
              <span v-if="props.amount" class="text-sm font-semibold">{{ props.amount }}</span>
              <div v-if="props.trailingIcon" class="size-4">
                <ElIcon v-bind="props.trailingIcon" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="props.tags && props.tags.length > 0" class="flex flex-row flex-wrap justify-end gap-2 mt-3">
        <ElTag v-for="tag in props.tags" :key="tag.key ?? tag.text" :color="tag.color" :text="tag.text" />
      </div>
      <div class="h-4" />
      <div v-if="props.modelValue === undefined" class="border-b border-b-neutral-surface" />
    </div>
  </li>
</template>
